"use client";

import { useEffect, useState, Fragment } from "react";
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Button, TextField,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { collection, getDocs, addDoc, doc, setDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import { useAuth } from "../AuthContext";
import Footer from "../components/Footer";

const SECTIONS = [
  { key: "general", label: "General" },
  { key: "rewards", label: "Rewards" },
  { key: "hotel", label: "Hotel" },
  { key: "wellness", label: "Wellness Center" },
  { key: "pavilion", label: "PB Pavilion" },
];

// Answers support lightweight formatting (parsed below, never injected as raw HTML):
//   blank line        -> new paragraph
//   "- item"          -> bullet point
//   **bold text**     -> bold
//   [link text](/url) -> link
//   a line of just "---" -> divider
const DEFAULT_FAQS = [
  { id: "general-0", section: "general", order: 0,
    q: "How do I create an account?",
    a: "Click on Account → Sign Up in the navbar. Enter your name, email, and password, then verify your email address if prompted. Once verified, you can sign in and start booking." },
  { id: "general-1", section: "general", order: 1,
    q: "How do I sign in to my account?",
    a: "Use the Account → Sign In link in the navbar. Enter your email and password. If you forgot your password, use the Forgot Password link on the sign-in page to reset it." },
  { id: "general-2", section: "general", order: 2,
    q: "Can I modify my account information?",
    a: "Yes. Sign in to your account and go to Account → Profile to update your personal information, contact details, and preferences." },
  { id: "general-3", section: "general", order: 3,
    q: "How do I reset my password?",
    a: "On the sign-in page, click 'Forgot Password'. Enter your email address, and you'll receive a link to reset your password. Follow the instructions in the email." },
  { id: "general-4", section: "general", order: 4,
    q: "Is my personal information secure?",
    a: "Yes. We use industry-standard encryption and security measures to protect your data. View our Privacy Policy for more details on how we handle your information." },
  { id: "general-5", section: "general", order: 5,
    q: "Can I book services without creating an account?",
    a: "While you can browse our offerings, you'll need to create an account to complete any reservations for rooms, wellness services, or dining experiences." },
  { id: "general-6", section: "general", order: 6,
    q: "Are there any age restrictions?",
    a: "Guests must be at least 18 to book a suite/villa, enter the wellness center and/or drink alcohol at the PB Pavilion." },

  { id: "rewards-0", section: "rewards", order: 0, _v: 2,
    q: "How does your rewards program operate?",
    a: `**Earn points. Unlock exclusive experiences.**

Every dollar you spend earns 1 point — automatically, with no action required on your part.

Accumulate points across all Agape Prime Hotel services:

- Suites & Villa stays
- Wellness Center sessions and treatments
- PB Pavilion dining experiences

---

**AVAILABLE REWARDS**

**250 Points — Complimentary Breakfast for Two**
Enjoy a full breakfast for two at our restaurant. Show your confirmation to staff at check-in.

**500 Points — Agape Spa Credit ($50 off)**
Redeem a $50 credit toward any Wellness Center service on your next visit.

**1,000 Points — Complimentary Room Upgrade**
Upgrade your current booking to a higher suite tier at no extra cost, subject to availability.

**2,500 Points — Complimentary Wellness Session**
One full 60-minute wellness session of your choice at the Agape Wellness Center.

**5,000 Points — Free Night's Stay**
One complimentary night in a select suite or villa, subject to availability.

**10,000 Points — Luxury Experience Package**
The full Agape experience: one night's stay, a Wellness session, and a PB Pavilion dinner for two.

---

Your points never expire as long as your account remains active.

Track your balance and redeem rewards on the [Rewards page](/points).` },
  { id: "rewards-1", section: "rewards", order: 1, _v: 2,
    q: "How do I earn and redeem points?",
    a: `**Earning Points**

Points are awarded automatically after every successful booking. Every dollar spent earns 1 point, applied to your account immediately after checkout is confirmed — no codes or manual steps needed.

**Redeeming Rewards**

You can redeem any reward you currently qualify for. Here is how it works:

- Visit the [Rewards page](/points) and sign in to your account.
- Any reward you have enough points for will show a "Redeem" button.
- Click "Redeem," confirm your selection, and your balance updates instantly.
- Redeeming a reward deducts only that reward's point cost — your remaining balance carries forward automatically.
- You can continue earning and saving toward future rewards right away.

**Your Points Never Expire**

Points remain on your account indefinitely. There is no deadline or inactivity penalty.

**Redemption History**

Every earned and redeemed transaction is logged in your Points History, visible on the [Rewards page](/points). Cart checkouts show a full per-item breakdown.` },
  { id: "rewards-2", section: "rewards", order: 2, _v: 1,
    q: "Do I need to use the same email on Agape Prime Hotel and PB Pavilion?",
    a: `Yes. Rewards points are shared between Agape Prime Hotel and PB Pavilion, and accounts are linked automatically by email address.

To make sure your points are combined into one shared balance, use the same email address when creating your account on both applications. If you sign up with different email addresses on each app, you'll end up with two separate, unlinked balances.` },

  { id: "hotel-0", section: "hotel", order: 0,
    q: "How to search for housing?",
    a: "Visit the Suites & Villas page, use the filters for date, number of guests, and amenities, then open a listing to view details." },
  { id: "hotel-1", section: "hotel", order: 1,
    q: "How do I make a reservation?",
    a: "Reservations can be made [here](/rooms)." },
  { id: "hotel-2", section: "hotel", order: 2,
    q: "Why should I pay for a VIP subscription?",
    a: "VIP subscribers get early exclusive access, members-only rates, and complimentary upgrades when available. VIP members can check in/check out the room earlier/later than normal check in (subject to availability). They can also get a % off the bill at checkout if VIP. And, we can upgrade their room subject to availability." },
  { id: "hotel-3", section: "hotel", order: 3,
    q: "What's the procedure to obtain VIP membership?",
    a: "Apply via Account → Points or contact us to review qualification details." },
  { id: "hotel-4", section: "hotel", order: 4,
    q: "What amenities are included?",
    a: "Spa, wellness center, pools, fine dining, room service, and more." },
  { id: "hotel-5", section: "hotel", order: 5,
    q: "Do you offer airport transfers?",
    a: "Yes. Private airport transfers are available-contact us  for scheduling and rates." },

  { id: "wellness-0", section: "wellness", order: 0,
    q: "How does the Wellness center work?",
    a: "The wellness center provides a variety of wellness amenities that guests can access via a general admission fee or by purchasing individual services. Upon arrival guests receive instructions regarding their locker and/or how to charge additional items and services which can be paid for at checkout. The wellness center includes access to hydrotherapy pools, an infrared sauna, lounge area, and a fitness center." },
  { id: "wellness-1", section: "wellness", order: 1,
    q: "How do I make a reservation?",
    a: "Reservations can be made [here](/wellness)." },
  { id: "wellness-2", section: "wellness", order: 2,
    q: "What is included with general admission?",
    a: "The wellness center provides a variety of wellness amenities that guests can pay for through a general admission fee or by purchasing individual services. Guests receive instructions upon arrival regarding their locker and/or how to charge additional items and services, which can be paid for at checkout. The experience includes access to hydrotherapy pools, an infrared sauna, lounge area, and a fitness center." },
  { id: "wellness-3", section: "wellness", order: 3,
    q: "What amenities are included with general admission?",
    a: "Hydrotherapy pools with strategically placed jets designed to target pressure points and improve circulation. Multiple unique saunas, including dry saunas, Infrared Zone, Halotherapy, and more. Several lounge areas for resting and relaxing. A fitness center is available for use." },
  { id: "wellness-4", section: "wellness", order: 4,
    q: "What additional services are available?",
    a: "Professional spa services are available for an extra charge, including body massages (deep tissue, hot stone), body scrubs (traditional body scrubs done in a no-frills environment), and customizable facials to address skin concerns. The PB Pavilion is available for guests to purchase food and drinks." },
  { id: "wellness-5", section: "wellness", order: 5,
    q: "What are the benefits of a Wellness Center VIP membership?",
    a: "A Wellness Center VIP membership typically includes perks like priority booking, exclusive discounts on services and products, and access to member-only events and amenities." },

  { id: "pavilion-0", section: "pavilion", order: 0,
    q: "How does PB Pavilion work?",
    a: "Our chef's choice experience is reservation-only and guests are encouraged to communicate allergies and preferences in advance. A la carte items are available only after the chef's choice experience." },
  { id: "pavilion-1", section: "pavilion", order: 1,
    q: "How do I make a reservation?",
    a: "Reservations can be made [here](/pbpavilion)." },
  { id: "pavilion-2", section: "pavilion", order: 2,
    q: "What is the menu and dining experience?",
    a: "PB Pavilion offers a timed, chef's choice experience.There are different types of course options. You can order additional items from the a la carte menu, but only after the chef's choice has concluded. You must inform us of any food allergies or dietary restrictions when booking, as some restrictions (like vegetarian, vegan, or gluten-free) cannot be accommodated. Due to the daily changing menu, we cannot guarantee accommodation 'with' advance notice.." },
  { id: "pavilion-3", section: "pavilion", order: 3,
    q: "What is the atmosphere and dress code?",
    a: "The restaurant has a 'speakeasy' style with an African diaspora influenced aesthetic. The dress code is casual for lunch/dinner. You are invited to enjoy drinks before or after your meal." },
  { id: "pavilion-4", section: "pavilion", order: 4,
    q: "What are the benefits of a PB Pavilion VIP membership?",
    a: "Benefits often include exclusive perks like priority reservations, special event invitations, and members-only discounts." },
];

// Parse **bold** and [text](url) into React nodes — never raw HTML, so no injection risk
const parseInline = (text, keyPrefix) => {
  const parts = [];
  const regex = /\*\*(.+?)\*\*|\[(.+?)\]\((.+?)\)/g;
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    if (match[1] !== undefined) {
      parts.push(<strong key={`${keyPrefix}-b-${key++}`}>{match[1]}</strong>);
    } else {
      parts.push(
        <a key={`${keyPrefix}-a-${key++}`} href={match[3]} style={{ color: "#005792", textDecoration: "underline" }}>
          {match[2]}
        </a>
      );
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

const renderAnswer = (text) => {
  const blocks = String(text || "").split(/\n\s*\n/);
  return blocks.map((block, bi) => {
    const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return null;

    if (lines.length === 1 && lines[0] === "---") {
      return <div key={bi} style={{ borderTop: "1px solid rgba(0,0,0,0.1)", margin: "16px 0" }} />;
    }

    const isList = lines.every((l) => l.startsWith("- "));
    if (isList) {
      return (
        <ul key={bi} style={{ marginLeft: 20, marginBottom: 16 }}>
          {lines.map((l, li) => (
            <li key={li}>{parseInline(l.slice(2), `${bi}-${li}`)}</li>
          ))}
        </ul>
      );
    }

    return (
      <p key={bi} style={{ marginBottom: 12 }}>
        {lines.map((l, li) => (
          <Fragment key={li}>
            {parseInline(l, `${bi}-${li}`)}
            {li < lines.length - 1 && <br />}
          </Fragment>
        ))}
      </p>
    );
  });
};

export default function Faq() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);

  const page = {
    position: "relative",
    minHeight: "calc(100vh - 120px)",
    width: "100%",
  };

  const videoBg = {
    position: "fixed",
    inset: 0,
    width: "100vw",
    height: "100vh",
    objectFit: "cover",
    zIndex: -2,
  };

  const wrap = {
    minHeight: "calc(100vh - 120px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 16px",
  };

  //Make panel match the header container width so "FAQ" is centered above the bubble
  const panel = {
    width: "100%", // take full width of the inner container
    maxWidth: "900px", // optional cap so it doesn't grow too big on very large screens
    borderRadius: "16px",
    padding: "22px",
    background: "rgba(255,255,255,0.18)",
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    border: "1px solid rgba(255,255,255,0.45)",
    boxShadow: "0 16px 48px rgba(0,0,0,0.35)",
    margin: "0 auto", // center the panel itself
  };

  const btnBase = {
    background: "rgba(255,255,255,0.55)",
    border: "1px solid rgba(0,0,0,0.10)",
    borderRadius: "12px",
    color: "#111",
    transition: "transform 220ms ease, background 220ms ease, border-color 220ms ease",
  };

  const sectionHeading = {
    fontFamily: "'Satoshi', 'SF Pro Display', 'Inter', system-ui, sans-serif",
    fontWeight: 700,
    fontSize: "2.2rem",
    letterSpacing: "-0.02em",
    color: "#111",
    marginTop: "40px",
    marginBottom: "8px",
    textAlign: "left",
    textShadow: "0 1px 3px rgba(0,0,0,0.1)",
  };

  const sectionAccent = {
    width: "60px",
    height: "3px",
    background: "linear-gradient(90deg, #002147 0%, #005792 100%)",
    borderRadius: "2px",
    marginBottom: "20px",
    opacity: 0.7,
  };

  // ── Load / seed ──────────────────────────────────────────────────────
  // Items in DEFAULT_FAQS that carry a _v (version) field are auto-migrated
  // in Firestore when the stored _v is older than the code version.
  // This lets code changes push new default content to the live database
  // while preserving any admin edits made to other FAQ items.
  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(collection(db, "faqItems"));
        if (snap.empty) {
          await Promise.all(DEFAULT_FAQS.map((f) => setDoc(doc(db, "faqItems", f.id), f)));
          setItems(DEFAULT_FAQS);
        } else {
          let data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

          // Version-bump migration: update any versioned defaults whose _v is stale
          const versionedDefaults = DEFAULT_FAQS.filter((f) => f._v != null);
          const patches = [];
          for (const def of versionedDefaults) {
            const stored = data.find((d) => d.id === def.id);
            if (!stored || (stored._v ?? 0) < def._v) {
              patches.push(setDoc(doc(db, "faqItems", def.id), def));
            }
          }
          if (patches.length > 0) {
            await Promise.all(patches);
            // Refresh local state with patched items
            data = data.map((d) => {
              const patched = versionedDefaults.find((def) => def.id === d.id && (d._v ?? 0) < def._v);
              return patched ? { ...patched } : d;
            });
            // Add any versioned items that weren't in Firestore yet
            for (const def of versionedDefaults) {
              if (!data.find((d) => d.id === def.id)) data.push({ ...def });
            }
          }

          setItems(data);
        }
      } catch (err) {
        console.error(err);
        setItems(DEFAULT_FAQS);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const sectionItems = (key) =>
    items.filter((i) => i.section === key).sort((a, b) => a.order - b.order);

  // ── Edit / Add / Delete ──────────────────────────────────────────────
  const handleEditOpen = (item) => {
    setEditItem(item);
    setEditForm({ q: item.q || "", a: item.a || "" });
    setSavedMsg(false);
  };

  const handleEditClose = () => {
    setEditItem(null);
    setEditForm({});
    setSavedMsg(false);
  };

  const handleAddNew = async (sectionKey) => {
    const existing = sectionItems(sectionKey);
    const nextOrder = existing.length ? Math.max(...existing.map((i) => i.order)) + 1 : 0;
    const newItem = { section: sectionKey, order: nextOrder, q: "New question?", a: "Answer goes here." };
    try {
      const ref = await addDoc(collection(db, "faqItems"), newItem);
      const created = { id: ref.id, ...newItem };
      setItems((prev) => [...prev, created]);
      handleEditOpen(created);
    } catch (err) {
      console.error(err);
    }
  };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      const updated = { q: editForm.q, a: editForm.a };
      await updateDoc(doc(db, "faqItems", editItem.id), updated);
      setItems((prev) => prev.map((i) => (i.id === editItem.id ? { ...i, ...updated } : i)));
      setSavedMsg(true);
      setTimeout(handleEditClose, 1000);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this FAQ question? This cannot be undone.")) return;
    setSaving(true);
    try {
      await deleteDoc(doc(db, "faqItems", editItem.id));
      setItems((prev) => prev.filter((i) => i.id !== editItem.id));
      handleEditClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Re-run the hash-scroll once data has actually loaded and rendered
  useEffect(() => {
    if (loading) return;
    const hash = window.location.hash;
    if (hash === "#rewardsAccordion") {
      const element = document.getElementById("rewardsAccordion");
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        setTimeout(() => {
          const firstAccordionButton = element.querySelector(".accordion-button");
          if (firstAccordionButton && firstAccordionButton.classList.contains("collapsed")) {
            firstAccordionButton.click();
          }
        }, 500);
      }
    }
  }, [loading]);

  return (
    <>
      <div style={page} className="faq-page-enter">
        {/* Fullscreen video background */}
        <video src="/videos/sunsetbluedark.mp4" autoPlay muted loop playsInline style={videoBg} />

        <div style={wrap}>
          {/* Inner container that everything (including the bubble) centers inside */}
          <div style={{ width: "100%", maxWidth: 840, margin: "0 auto", textAlign: "center" }}>
            <h2
              className="faq-heading-pop"
              style={{
                textAlign: "center",
                fontFamily: "'Satoshi', 'SF Pro Display', 'Inter', system-ui, sans-serif",
                fontWeight: 700,
                letterSpacing: "-0.02em",
                color: "#414040ff",
                textShadow: "0 2px 18px #fff6",
                fontSize: "3.5rem",
                marginBottom: 8,
              }}
            >
              FAQ
            </h2>

            {/* Accent "bubble" – now perfectly centered under FAQ within the same container */}
            <div
              style={{
                width: 90,
                height: 4,
                background: "linear-gradient(90deg, #002147 0%, #005792 100%)",
                borderRadius: 2,
                margin: "18px auto 32px auto",
                opacity: 0.6,
              }}
            />

            <div style={panel} className="faq-panel-rise">
              {loading ? (
                <p style={{ textAlign: "center", color: "#111" }}>Loading...</p>
              ) : (
                SECTIONS.map((section) => (
                  <div key={section.key}>
                    <h3 style={sectionHeading}>{section.label}</h3>
                    <div style={sectionAccent} />
                    <div className="accordion" id={`${section.key}Accordion`} style={{ display: "grid", gap: 12 }}>
                      {sectionItems(section.key).map((item) => {
                        const collapseId = `collapse-${item.id}`;
                        const headingId = `heading-${item.id}`;
                        return (
                          <div key={item.id} className="accordion-item" style={{ border: "none", background: "transparent" }}>
                            <h2 className="accordion-header" id={headingId} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <button
                                className="accordion-button collapsed faq-acc-btn"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target={`#${collapseId}`}
                                aria-expanded="false"
                                aria-controls={collapseId}
                                style={{ ...btnBase, boxShadow: "none", flex: 1 }}
                              >
                                {item.q}
                              </button>
                              {isAdmin && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); handleEditOpen(item); }}
                                  sx={{ backgroundColor: "rgba(255,255,255,0.92)", width: 30, height: 30, flexShrink: 0, boxShadow: "0 2px 8px rgba(0,0,0,0.3)", "&:hover": { backgroundColor: "#fff", transform: "scale(1.1)" } }}
                                >
                                  <EditIcon sx={{ fontSize: 15, color: "#0d1b4c" }} />
                                </IconButton>
                              )}
                            </h2>
                            <div
                              id={collapseId}
                              className="accordion-collapse collapse"
                              aria-labelledby={headingId}
                              data-bs-parent={`#${section.key}Accordion`}
                            >
                              <div className="accordion-body faq-body-fade" style={{ background: "transparent", textAlign: "left" }}>
                                {renderAnswer(item.a)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {isAdmin && (
                      <Button
                        onClick={() => handleAddNew(section.key)}
                        startIcon={<AddIcon />}
                        sx={{ ...btnBase, boxShadow: "none", mt: 1.5, textTransform: "none" }}
                      >
                        Add Question
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Admin Edit Dialog */}
        <Dialog open={Boolean(editItem)} onClose={handleEditClose} maxWidth="sm" fullWidth
          PaperProps={{ sx: { backgroundColor: "#0d1b4c", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "16px" } }}>
          {editItem && (
            <>
              <DialogTitle sx={{ fontFamily: "Satoshi, Inter, sans-serif", fontWeight: 600, color: "white", display: "flex", alignItems: "center", gap: 1 }}>
                <EditIcon sx={{ fontSize: 18 }} /> Edit FAQ
                <IconButton onClick={handleEditClose} sx={{ position: "absolute", right: 8, top: 8, color: "white" }}><Close /></IconButton>
              </DialogTitle>

              <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
                <TextField label="Question" value={editForm.q || ""} onChange={(e) => setEditForm((p) => ({ ...p, q: e.target.value }))} fullWidth size="small"
                  helperText='The question shown as the clickable header — e.g. "How do I create an account?"'
                  InputLabelProps={{ style: { color: "rgba(255,255,255,0.6)" } }}
                  InputProps={{ style: { color: "white" } }}
                  FormHelperTextProps={{ style: { color: "rgba(255,255,255,0.45)", fontSize: "0.72rem" } }}
                  sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "rgba(255,255,255,0.2)" }, "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" }, "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.8)" } } }}
                />

                <TextField label="Answer" value={editForm.a || ""} onChange={(e) => setEditForm((p) => ({ ...p, a: e.target.value }))} fullWidth multiline rows={10}
                  helperText='Blank line = new paragraph · "- item" = bullet point · **bold** = bold text · [link text](/page) = clickable link · a line with just --- = divider'
                  InputLabelProps={{ style: { color: "rgba(255,255,255,0.6)" } }}
                  InputProps={{ style: { color: "white", fontSize: "0.85rem", fontFamily: "monospace" } }}
                  FormHelperTextProps={{ style: { color: "rgba(255,255,255,0.45)", fontSize: "0.72rem" } }}
                  sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "rgba(255,255,255,0.2)" }, "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" }, "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.8)" } } }}
                />
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
                <Button onClick={handleDelete} disabled={saving} startIcon={<DeleteIcon />}
                  sx={{ color: "#ff8a8a", "&:hover": { backgroundColor: "rgba(255,0,0,0.08)" } }}>
                  Delete
                </Button>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button onClick={handleEditClose} sx={{ color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px" }}>Cancel</Button>
                  <Button variant="contained" onClick={handleEditSave} disabled={saving}
                    sx={{ backgroundColor: savedMsg ? "#1a7a4a" : "white", color: savedMsg ? "white" : "#0d1b4c", fontWeight: 600, borderRadius: "8px", "&:hover": { backgroundColor: savedMsg ? "#1a7a4a" : "#f0f0f0" } }}>
                    {saving ? "Saving..." : savedMsg ? "✓ Saved!" : "Save Changes"}
                  </Button>
                </Box>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Local CSS for animations */}
        <style>{`
          /* page fade-in */
          .faq-page-enter {
              animation: faqFadeIn 550ms ease both;
          }
          @keyframes faqFadeIn {
              from { opacity: 0 }
              to { opacity: 1 }
          }

          /* heading pop-in */
          .faq-heading-pop {
              transform-origin: center;
              animation: faqPop 520ms cubic-bezier(.16,.84,.44,1) 120ms both;
          }
          @keyframes faqPop {
              0% { opacity: 0; transform: translateY(8px) scale(.98) }
              100% { opacity: 1; transform: translateY(0) scale(1) }
          }

          /* panel rise / glass card */
          .faq-panel-rise {
              animation: faqRise 640ms cubic-bezier(.2,.8,.2,1) 140ms both;
          }
          @keyframes faqRise {
              0% { opacity: 0; transform: translateY(16px) }
              100% { opacity: 1; transform: translateY(0) }
          }

          /* accordion button micro-interactions */
          .faq-acc-btn:hover {
              transform: translateY(-1px);
              background: rgba(255,255,255,0.18) !important;
              border-color: rgba(0,0,0,0.12) !important;
          }
          .faq-acc-btn:focus {
              box-shadow: 0 0 0 0.2rem rgba(255,255,255,0.25) !important;
          }

          /* fade content when opening */
          .accordion-collapse.show .faq-body-fade {
              animation: faqBodyFade 260ms ease both;
          }
          @keyframes faqBodyFade {
              from { opacity: 0; transform: translateY(4px) }
              to { opacity: 1; transform: translateY(0) }
          }
        `}</style>
      </div>

      <Footer />
    </>
  );
}
